import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import useStyles from './styles';
import { Box, ButtonBase, Typography } from '@material-ui/core';
import { useSelectedTheme, useTranslate } from '../../../../../Hooks';
import { useVerticalNav } from '../../../../../Contexts/VerticalNavContext';
import {
  GetLookupItemsByLookupTypeName,
  GetPropertyMarketingByPropertyId,
  CreatePropertyMarketing,
} from '../../../../../Services';
import { GetParams } from '../../../../../Helper';
import {
  CustomRangeSlider,
  ReskinningAutocomplete,
  ReskinningInputs,
  CPP_MarketingAllComponentSkeleton,
} from '../../../../../Components';
import { AI_PropertyMarketing } from '../../../../../Services/AIServices/AIServices';
import CustomDialog from '../../../../../Components/V2/CustomDialog/CustomDialog';
import { CompletePropertyProfileContext } from '../../../../../Contexts/CompletePropertyProfileContext/CompletePropertyProfileContext';
import Divider from './Divider';
import Stars02Icon from '../../../../../assets/icons/Stars02Icon';

const filter = {
  pageIndex: 1,
  pageSize: 999,
};

function CPP_MarketingAllComponent({ propertyId, stepId, onStepComplete }) {
  const styles = useStyles();
  const { setAlertBoxContent } = useVerticalNav();
  const context = useContext(CompletePropertyProfileContext);
  const [isOpenAIContentGeneratorDialog, setisOpenAIContentGeneratorDialog] =
    useState(false);
  const [aiGeneratedContent, setAiGeneratedContent] = useState({
    englishTitle: '',
    englishDescription: '',
    arabicTitle: '',
    arabicDescription: '',
    localAreaAndAmenitiesDescription: '',
    developerDescription: '',
    englishAddressLine: '',
    arabicAddressLine: '',
    additionalText: '',
  });
  const [aiRawContent, setAiRawContent] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [showRegenerateButton, setShowRegenerateButton] = useState(false);
  const [typingIntervals, setTypingIntervals] = useState([]);
  const {
    theme: { palette },
  } = useSelectedTheme();
  const reducer = useCallback((state, action) => {
    if (!action || typeof action !== 'object') {
      return state;
    }

    if (action.id !== 'edit') {
      return {
        ...state,
        [action.id]: action.value,
      };
    }

    if (action.value && typeof action.value === 'object') {
      return {
        ...action.value,
      };
    }
    return state;
  }, []);
  const { translate } = useTranslate('CompletePropertyProfile');
  const [propertyMarketingId, setPropertyMarketingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [propertyMarketingData, setPropertyMarketingData] = useState(null);
  const [data, setData] = useReducer(reducer, {
    webProjectType: [],
    marketingWebLabel: [],
    uniqueSellingPoints: [],
  });
  const [values, setValues] = useReducer(reducer, {
    propertyId: propertyId,
    propertyOverView: null,
    localAreaAndAmenitiesDescription: null,
    webRemarks: null,
    isFeaturedSliderOnWeb: false,
    isFeaturedProjectOnWeb: false,
    propertyAdditionalDescription: null,
    englishMarketingTitle: null,
    arabicMarketingTitle: null,
    arabicAddressLine: null,
    arabicPropertyOverview: null,
    videoBannerURL: null,
    imageBannerURL: null,
    propertyLabelId: null,
    siteInfoAndAmenitiesDescription: null,
    developerDescription: null,
    isShowInWeb: false,
    marketingUsp1Id: null,
    marketingUsp2Id: null,
    marketingUsp3Id: null,
    marketingUsp4Id: null,
    luxuryIds: [],
    financeIds: [],
    styleIds: [],
    viewIds: [],
    isShowInMetaverse: false,
    priceMin: null,
    priceMax: null,
    sizeMin: null,
    sizeMax: null,
    webProjectTypeId: null,
    featuredProject: false,
    propertyMarketingId: null,
    englishAddressLine: null,
    availableBedrooms: null,
    launchYear: null,
    launchMonth: null,
    projectRating: null,
    price_SqFt: null,
    epectedROI: null,
    capitalAppreciate: null,
    vicinity: null,
    masterDeveloperContact: null,
    serviceCharge: null,
    securityDeposit: null,
    brochureMarketingImagesFileIds: [],
    publishPropertyName: null,
    webProjectTypeName: null,
  });

  async function getPropertyMarketingData() {
    setIsLoading(true);

    try {
      const res = await GetPropertyMarketingByPropertyId(propertyId);

      if (res && res.status && res.status >= 400) {
        setPropertyMarketingData(null);
        setPropertyMarketingId(null);
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: translate('failed-to-load-marketing-data'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        return;
      }

      if (res && Array.isArray(res) && res.length > 0) {
        const firstRecord = res[0];

        setPropertyMarketingData(res);
        setPropertyMarketingId(firstRecord.propertyMarketingId || null);

        const transformedData = {
          ...firstRecord,
          propertyId,
          luxuryIds:
            firstRecord.luxuryIds?.map(
              (luxItem) => luxItem.luxurysId || luxItem.luxuryId
            ) || [],
          financeIds:
            firstRecord.financeIds?.map(
              (finItem) => finItem.financesId || finItem.financeId
            ) || [],
          viewIds:
            firstRecord.viewIds?.map(
              (viewItem) => viewItem.viewsId || viewItem.viewId
            ) || [],
          styleIds:
            firstRecord.styleIds?.map(
              (styleItem) => styleItem.stylesId || styleItem.styleId
            ) || [],
          priceMin: firstRecord.priceFrom || firstRecord.priceMin || null,
          priceMax: firstRecord.priceMax || null,
          propertyOverView: firstRecord.propertyOverView || null,
          localAreaAndAmenitiesDescription:
            firstRecord.localAreaAndAmenitiesDescription || null,
          webRemarks: firstRecord.webRemarks || null,
          isFeaturedSliderOnWeb: firstRecord.isFeaturedSliderOnWeb || false,
          isFeaturedProjectOnWeb: firstRecord.isFeaturedProjectOnWeb || false,
          propertyAdditionalDescription:
            firstRecord.propertyAdditionalDescription || null,
          englishMarketingTitle: firstRecord.englishMarketingTitle || null,
          arabicMarketingTitle: firstRecord.arabicMarketingTitle || null,
          arabicAddressLine: firstRecord.arabicAddressLine || null,
          arabicPropertyOverview: firstRecord.arabicPropertyOverview || null,
          videoBannerURL: firstRecord.videoBannerURL || null,
          imageBannerURL: firstRecord.imageBannerURL || null,
          propertyLabelId: firstRecord.propertyLabelId || null,
          siteInfoAndAmenitiesDescription:
            firstRecord.siteInfoAndAmenitiesDescription || null,
          developerDescription: firstRecord.developerDescription || null,
          isShowInWeb: firstRecord.isShowInWeb || false,
          marketingUsp1Id: firstRecord.marketingUsp1Id || null,
          marketingUsp2Id: firstRecord.marketingUsp2Id || null,
          marketingUsp3Id: firstRecord.marketingUsp3Id || null,
          marketingUsp4Id: firstRecord.marketingUsp4Id || null,
          isShowInMetaverse: firstRecord.isShowInMetaverse || false,
          sizeMin: firstRecord.sizeMin || null,
          sizeMax: firstRecord.sizeMax || null,
          webProjectTypeId: firstRecord.webProjectTypeId || null,
          featuredProject: firstRecord.featuredProject || false,
          englishAddressLine: firstRecord.englishAddressLine || null,
          availableBedrooms: firstRecord.availableBedrooms || null,
          launchYear: firstRecord.launchYear || null,
          launchMonth: firstRecord.launchMonth || null,
          projectRating: firstRecord.projectRating || null,
          price_SqFt: firstRecord.price_SqFt || null,
          epectedROI: firstRecord.epectedROI || null,
          capitalAppreciate: firstRecord.capitalAppreciate || null,
          vicinity: firstRecord.vicinity || null,
          masterDeveloperContact: firstRecord.masterDeveloperContact || null,
          serviceCharge: firstRecord.serviceCharge || null,
          securityDeposit: firstRecord.securityDeposit || null,
          brochureMarketingImagesFileIds:
            firstRecord.brochureMarketingImagesFileIds || [],
          publishPropertyName: firstRecord.publishPropertyName || null,
          webProjectTypeName: firstRecord.webProjectTypeName || null,
        };

        setValues({
          id: 'edit',
          value: transformedData,
        });
      } else if (res && Array.isArray(res) && res.length === 0) {
        setPropertyMarketingData(null);
        setPropertyMarketingId(null);
        resetValuesToDefault();
      } else {
        setPropertyMarketingData(null);
        setPropertyMarketingId(null);
        resetValuesToDefault();
      }
    } catch (error) {
      setPropertyMarketingData(null);
      setPropertyMarketingId(null);
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: translate('failed-to-load-marketing-data'),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      resetValuesToDefault();
    }

    setIsLoading(false);
  }

  function resetValuesToDefault() {
    setValues({
      id: 'edit',
      value: {
        propertyId: propertyId,
        propertyOverView: null,
        localAreaAndAmenitiesDescription: null,
        webRemarks: null,
        isFeaturedSliderOnWeb: false,
        isFeaturedProjectOnWeb: false,
        propertyAdditionalDescription: null,
        englishMarketingTitle: null,
        arabicMarketingTitle: null,
        arabicAddressLine: null,
        arabicPropertyOverview: null,
        videoBannerURL: null,
        imageBannerURL: null,
        propertyLabelId: null,
        siteInfoAndAmenitiesDescription: null,
        developerDescription: null,
        isShowInWeb: false,
        marketingUsp1Id: null,
        marketingUsp2Id: null,
        marketingUsp3Id: null,
        marketingUsp4Id: null,
        luxuryIds: [],
        financeIds: [],
        styleIds: [],
        viewIds: [],
        isShowInMetaverse: false,
        priceMin: null,
        priceMax: null,
        sizeMin: null,
        sizeMax: null,
        webProjectTypeId: null,
        featuredProject: false,
        propertyMarketingId: null,
        englishAddressLine: null,
        availableBedrooms: null,
        launchYear: null,
        launchMonth: null,
        projectRating: null,
        price_SqFt: null,
        epectedROI: null,
        capitalAppreciate: null,
        vicinity: null,
        masterDeveloperContact: null,
        serviceCharge: null,
        securityDeposit: null,
        brochureMarketingImagesFileIds: [],
        publishPropertyName: null,
        webProjectTypeName: null,
      },
    });
  }

  async function fetchPropertyUSP() {
    try {
      const res = await GetLookupItemsByLookupTypeName({
        lookUpName: 'UniqueSellingPoints',
        pageSize: filter.pageSize,
        pageIndex: filter.pageIndex,
      });

      if (res && !res.status && res.result && Array.isArray(res.result)) {
        setData({ id: 'uniqueSellingPoints', value: res.result });
      } else {
        setData({ id: 'uniqueSellingPoints', value: [] });
      }
    } catch (error) {
      setData({ id: 'uniqueSellingPoints', value: [] });
    }
  }

  async function fetchMarketingWebLabel() {
    try {
      const res = await GetLookupItemsByLookupTypeName({
        lookUpName: 'MarketingWebLabel',
        pageSize: filter.pageSize,
        pageIndex: filter.pageIndex,
      });

      if (res && !res.status && res.result && Array.isArray(res.result)) {
        setData({ id: 'marketingWebLabel', value: res.result });
      } else {
        setData({ id: 'marketingWebLabel', value: [] });
      }
    } catch (error) {
      setData({ id: 'marketingWebLabel', value: [] });
    }
  }

  async function fetchWebProjectType() {
    try {
      const res = await GetLookupItemsByLookupTypeName({
        lookUpName: 'WebProjectType',
        pageSize: filter.pageSize,
        pageIndex: filter.pageIndex,
      });

      if (res && !res.status && res.result && Array.isArray(res.result)) {
        setData({ id: 'webProjectType', value: res.result });
      } else {
        setData({ id: 'webProjectType', value: [] });
      }
    } catch (error) {
      setData({ id: 'webProjectType', value: [] });
    }
  }

  function updateValues(key, newValue) {
    setValues({
      id: 'edit',
      value: {
        ...values,
        [key]: newValue,
      },
    });
  }

  const resetAIContent = () => {
    // Clear any existing typing intervals
    typingIntervals.forEach((interval) => clearInterval(interval));
    setTypingIntervals([]);

    setAiGeneratedContent({
      englishTitle: '',
      englishDescription: '',
      arabicTitle: '',
      arabicDescription: '',
      localAreaAndAmenitiesDescription: '',
      developerDescription: '',
      englishAddressLine: '',
      arabicAddressLine: '',
      additionalText: '',
    });
  };

  const simulateTypingEffect = (text, field) => {
    return new Promise((resolve) => {
      if (!text || typeof text !== 'string') {
        resolve();
        return;
      }

      let index = 0;
      const charsPerInterval = 8;
      const interval = setInterval(() => {
        if (index < text.length) {
          const nextChunk = text.slice(index, index + charsPerInterval);
          setAiGeneratedContent((prev) => ({
            ...prev,
            [field]: prev[field] + nextChunk,
          }));
          index += charsPerInterval;
        } else {
          clearInterval(interval);
          setTypingIntervals((prev) => prev.filter((i) => i !== interval));
          resolve();
        }
      }, 1);

      setTypingIntervals((prev) => [...prev, interval]);
    });
  };

  const startTypingSequence = async (content) => {
    try {
      await simulateTypingEffect(content.englishTitle || '', 'englishTitle');
      await simulateTypingEffect(
        content.englishDescription || '',
        'englishDescription'
      );
      await simulateTypingEffect(content.arabicTitle || '', 'arabicTitle');
      await simulateTypingEffect(
        content.arabicDescription || '',
        'arabicDescription'
      );
      await simulateTypingEffect(
        content.localAreaAndAmenitiesDescription || '',
        'localAreaAndAmenitiesDescription'
      );
      await simulateTypingEffect(
        content.developerDescription || '',
        'developerDescription'
      );
      await simulateTypingEffect(
        content.englishAddressLine || '',
        'englishAddressLine'
      );
      await simulateTypingEffect(
        content.arabicAddressLine || '',
        'arabicAddressLine'
      );
      await simulateTypingEffect(
        content.additionalText || '',
        'additionalText'
      );

      setIsGeneratingAI(false);
      setShowRegenerateButton(true);
    } catch (error) {
      setIsGeneratingAI(false);
      setShowRegenerateButton(true);
    }
  };

  const generateAIContent = useCallback(async () => {
    try {
      const response = await AI_PropertyMarketing(propertyId);
      if (response && !(response.status && response.status !== 200)) {
        setAiRawContent(response);
        startTypingSequence({
          englishTitle:
            response.englishTitle || response['English Marketing Title'] || '',
          englishDescription:
            response.englishDescription ||
            response['English Marketing Description'] ||
            '',
          arabicTitle:
            response.arabicTitle || response['Arabic Marketing Title'] || '',
          arabicDescription:
            response.arabicDescription ||
            response['Arabic Marketing Description'] ||
            '',
          localAreaAndAmenitiesDescription:
            response.localAreaAndAmenitiesDescription ||
            response['Local Area Description'] ||
            '',
          developerDescription:
            response.developerDescription ||
            response['Developer Description'] ||
            '',
          englishAddressLine:
            response.englishAddressLine ||
            response['English Address Line 1'] ||
            '',
          arabicAddressLine:
            response.arabicAddressLine ||
            response['Arabic Address Line 1'] ||
            '',
          additionalText:
            response.additionalText ||
            response['Additional Text Suggestions'] ||
            '',
        });
      } else {
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: translate('failed-to-generate-ai-content'),
          onClose: () => setAlertBoxContent(null),
        });
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: translate('failed-to-generate-ai-content'),
        onClose: () => setAlertBoxContent(null),
      });
    } finally {
      // Generating state will be set to false after typing animation completes
    }
  }, [propertyId, translate, setAlertBoxContent]);

  const applyAIContent = (content) => {
    if (content) {
      // Create a new values object with all AI content updates
      const updatedValues = {
        ...values,
        englishMarketingTitle:
          content.englishTitle || values.englishMarketingTitle,
        propertyOverView: content.englishDescription || values.propertyOverView,
        englishAddressLine:
          content.englishAddressLine || values.englishAddressLine,
        arabicMarketingTitle:
          content.arabicTitle || values.arabicMarketingTitle,
        arabicPropertyOverview:
          content.arabicDescription || values.arabicPropertyOverview,
        arabicAddressLine:
          content.arabicAddressLine || values.arabicAddressLine,
        localAreaAndAmenitiesDescription:
          content.localAreaAndAmenitiesDescription ||
          values.localAreaAndAmenitiesDescription,
        developerDescription:
          content.developerDescription || values.developerDescription,
        propertyAdditionalDescription:
          content.additionalText || values.propertyAdditionalDescription,
      };

      // Update all values at once
      setValues({
        id: 'edit',
        value: updatedValues,
      });

      setAlertBoxContent({
        display: true,
        variant: 'success',
        title: translate('ai-content-applied-successfully'),
        onClose: () => setAlertBoxContent(null),
      });
    }
  };

  const handleSave = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        propertyId: propertyId,
        propertyOverView: values.propertyOverView,
        localAreaAndAmenitiesDescription:
          values.localAreaAndAmenitiesDescription,
        webRemarks: values.webRemarks,
        siteInfoAndAmenitiesDescription: values.siteInfoAndAmenitiesDescription,
        developerDescription: values.developerDescription,
        isShowInWeb: values.isShowInWeb,
        isShowInMetaverse: values.isShowInMetaverse,
        luxuryIds: values.luxuryIds,
        financeIds: values.financeIds,
        styleIds: values.styleIds,
        viewIds: values.viewIds,
        isFeaturedSliderOnWeb: values.isFeaturedSliderOnWeb,
        isFeaturedProjectOnWeb: values.isFeaturedProjectOnWeb,
        propertyAdditionalDescription: values.propertyAdditionalDescription,
        englishMarketingTitle: values.englishMarketingTitle,
        arabicMarketingTitle: values.arabicMarketingTitle,
        arabicAddressLine: values.arabicAddressLine,
        arabicPropertyOverview: values.arabicPropertyOverview,
        videoBannerURL: values.videoBannerURL,
        imageBannerURL: values.imageBannerURL,
        propertyLabelId: values.propertyLabelId,
        marketingUsp1Id: values.marketingUsp1Id,
        marketingUsp2Id: values.marketingUsp2Id,
        marketingUsp3Id: values.marketingUsp3Id,
        marketingUsp4Id: values.marketingUsp4Id,
        priceMin: values.priceMin,
        priceMax: values.priceMax,
        sizeMin: values.sizeMin,
        sizeMax: values.sizeMax,
        webProjectTypeId: values.webProjectTypeId,
        featuredProject: values.featuredProject,
        englishAddressLine: values.englishAddressLine,
        availableBedrooms: values.availableBedrooms,
        launchYear: values.launchYear,
        launchMonth: values.launchMonth,
        projectRating: values.projectRating,
        price_SqFt: values.price_SqFt,
        epectedROI: values.epectedROI,
        capitalAppreciate: values.capitalAppreciate,
        vicinity: values.vicinity,
        masterDeveloperContact: values.masterDeveloperContact,
        serviceCharge: values.serviceCharge,
        securityDeposit: values.securityDeposit,
        brochureMarketingImagesFileIds: values.brochureMarketingImagesFileIds,
        publishPropertyName: values.publishPropertyName,
        webProjectTypeName: values.webProjectTypeName,
      };

      if (propertyMarketingId !== null) {
        payload.propertyMarketingId = propertyMarketingId;
      }

      const response = await CreatePropertyMarketing(payload);

      if (!(response && response.status && response.status !== 200)) {
        if (propertyMarketingId) {
          setAlertBoxContent({
            display: true,
            variant: 'success',
            title: translate('marketing-data-updated-successfully'),
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
        } else {
          setAlertBoxContent({
            display: true,
            variant: 'success',
            title: translate('marketing-data-created-successfully'),
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
        }
        getPropertyMarketingData();
        return { success: true };
      } else {
        if (propertyMarketingId) {
          setAlertBoxContent({
            display: true,
            variant: 'error',
            title: translate('failed-to-update-marketing-data'),
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
        } else {
          setAlertBoxContent({
            display: true,
            variant: 'error',
            title: translate('failed-to-create-marketing-data'),
            onClose: () => {
              setAlertBoxContent(null);
            },
          });
        }
        return {
          success: false,
          error: propertyMarketingId
            ? 'Failed to update marketing data'
            : 'Failed to create marketing data',
        };
      }
    } catch (error) {
      if (propertyMarketingId) {
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: translate('failed-to-update-marketing-data'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      } else {
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: translate('failed-to-create-marketing-data'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, values, propertyMarketingId, translate, setAlertBoxContent]);

  const validateForm = useCallback(() => {
    // Marketing data is optional, no strict validation required
    return { isValid: true, errors: [] };
  }, []);

  const onSaveClicked = useCallback(async () => {
    const result = await handleSave();
    if (result && result.success && onStepComplete) {
      onStepComplete();
    }
    return result;
  }, [handleSave, onStepComplete]);

  const onNextClicked = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }
    return await onSaveClicked();
  }, [onSaveClicked, validateForm]);

  useEffect(() => {
    if (propertyId) {
      getPropertyMarketingData();
      fetchPropertyUSP();
      fetchWebProjectType();
      fetchMarketingWebLabel();
    }
  }, [propertyId]);

  useEffect(() => {
    if (context?.setSaveHandler) context.setSaveHandler(onSaveClicked);
    if (context?.setNextHandler) context.setNextHandler(onNextClicked);
    if (context?.setValidationHandler)
      context.setValidationHandler(validateForm);

    return () => {
      if (context?.setSaveHandler) context.setSaveHandler(null);
      if (context?.setNextHandler) context.setNextHandler(null);
      if (context?.setValidationHandler) context.setValidationHandler(null);
    };
  }, [onSaveClicked, onNextClicked, validateForm, context]);

  useEffect(() => {
    if (context?.setIsSaving) {
      context.setIsSaving(isLoading);
    }
  }, [isLoading, context]);

  // Cleanup typing intervals on unmount
  useEffect(() => {
    return () => {
      typingIntervals.forEach((interval) => clearInterval(interval));
    };
  }, []);

  return isLoading ? (
    <CPP_MarketingAllComponentSkeleton />
  ) : (
    <>
      <Box className={styles.CPP_MarketingAllComponentWrapper}>
        <Box className={styles.headerSection}>
          <Box className={styles.headerTitle}>
            <span>{translate('marketing-title-and-desc')}</span>
          </Box>
          <Box className={styles.aiGenerateButtonSection}>
            <ButtonBase
              className={styles.aiGenerateButton}
              onClick={() => {
                setisOpenAIContentGeneratorDialog(true);
                setIsGeneratingAI(true);
                setShowRegenerateButton(false);
                resetAIContent();
                generateAIContent();
              }}
              style={{ cursor: 'pointer', position: 'relative', zIndex: 1 }}
            >
              <span className={styles.aiGenerateButtonContent}>
                <Stars02Icon
                  width='24'
                  height='24'
                  fill={palette.button.tertiary_fg}
                />
                {translate('generate-with-AI')}
              </span>
            </ButtonBase>
          </Box>
        </Box>
        <Divider />
        <Box className={styles.propertyLabelSection}>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('propert-label')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningAutocomplete
                idRef={'propertyLabelRef'}
                data={data.marketingWebLabel}
                inputPlaceholder={translate('select')}
                withoutSearchButton
                isLoading={false}
                multiple={false}
                selectedValues={
                  data.marketingWebLabel?.find(
                    (item) => item.lookupItemId === values.propertyLabelId
                  ) || null
                }
                displayLabel={(option) => option?.lookupItemName || ''}
                chipsLabel={(option) => option?.lookupItemName || ''}
                getOptionSelected={(option, value) =>
                  option?.lookupItemId === value?.lookupItemId
                }
                onChange={(_e, value) => {
                  updateValues('propertyLabelId', value?.lookupItemId);
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box className={styles.priceAndSizeSection}>
          <Box className={styles.priceField}>
            <span>{translate('price')}</span>
            <CustomRangeSlider
              value={[
                Number(values?.priceMin || 0),
                Number(values?.priceMax || 1000000000),
              ]}
              handleChange={(e, newValue) => {
                setValues({
                  id: 'edit',
                  value: {
                    ...values,
                    priceMin: newValue?.[0]?.toString(),
                    priceMax: newValue?.[1]?.toString(),
                  },
                });
              }}
              min={0}
              max={1000000000}
              valueLabelDisplay='off'
              preInputText={translate('AED')}
            />
          </Box>
          <Divider />
          <Box className={styles.sizeField}>
            <span>{translate('size')}</span>
            <CustomRangeSlider
              value={[
                Number(values?.sizeMin || 0),
                Number(values?.sizeMax || 500000),
              ]}
              handleChange={(e, newValue) => {
                setValues({
                  id: 'edit',
                  value: {
                    ...values,
                    sizeMin: newValue?.[0]?.toString(),
                    sizeMax: newValue?.[1]?.toString(),
                  },
                });
              }}
              min={0}
              max={500000}
              valueLabelDisplay='off'
              preInputText={translate('ft')}
            />
          </Box>
        </Box>

        <Box className={styles.marketingTitleSection}>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-title-en')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningInputs
                idRef='marketing-title-en'
                inputPlaceholder={translate('enter')}
                value={values.englishMarketingTitle || ''}
                onInputChanged={(e) => {
                  updateValues('englishMarketingTitle', e.target.value);
                }}
              />
            </Box>
          </Box>
          <Divider />
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-title-ar')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningInputs
                idRef='marketing-title-ar'
                inputPlaceholder={translate('enter')}
                value={values.arabicMarketingTitle || ''}
                onInputChanged={(e) => {
                  updateValues('arabicMarketingTitle', e.target.value);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={styles.marketingUSP_Section}>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-usp-1')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningAutocomplete
                idRef={'marketingUsp1Ref'}
                data={data.uniqueSellingPoints}
                inputPlaceholder={translate('select')}
                withoutSearchButton
                isLoading={false}
                multiple={false}
                selectedValues={
                  data.uniqueSellingPoints?.find(
                    (item) => item.lookupItemId === values.marketingUsp1Id
                  ) || null
                }
                displayLabel={(option) => option?.lookupItemName || ''}
                chipsLabel={(option) => option?.lookupItemName || ''}
                getOptionSelected={(option, value) =>
                  option?.lookupItemId === value?.lookupItemId
                }
                onChange={(_e, value) => {
                  updateValues('marketingUsp1Id', value?.lookupItemId);
                }}
              />
            </Box>
          </Box>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-usp-2')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningAutocomplete
                idRef={'marketingUsp2Ref'}
                data={data.uniqueSellingPoints}
                inputPlaceholder={translate('select')}
                withoutSearchButton
                isLoading={false}
                multiple={false}
                selectedValues={
                  data.uniqueSellingPoints?.find(
                    (item) => item.lookupItemId === values.marketingUsp2Id
                  ) || null
                }
                displayLabel={(option) => option?.lookupItemName || ''}
                chipsLabel={(option) => option?.lookupItemName || ''}
                getOptionSelected={(option, value) =>
                  option?.lookupItemId === value?.lookupItemId
                }
                onChange={(_e, value) => {
                  updateValues('marketingUsp2Id', value?.lookupItemId);
                }}
              />
            </Box>
          </Box>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-usp-3')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningAutocomplete
                idRef={'marketingUsp3Ref'}
                data={data.uniqueSellingPoints}
                inputPlaceholder={translate('select')}
                withoutSearchButton
                isLoading={false}
                multiple={false}
                selectedValues={
                  data.uniqueSellingPoints?.find(
                    (item) => item.lookupItemId === values.marketingUsp3Id
                  ) || null
                }
                displayLabel={(option) => option?.lookupItemName || ''}
                chipsLabel={(option) => option?.lookupItemName || ''}
                getOptionSelected={(option, value) =>
                  option?.lookupItemId === value?.lookupItemId
                }
                onChange={(_e, value) => {
                  updateValues('marketingUsp3Id', value?.lookupItemId);
                }}
              />
            </Box>
          </Box>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-usp-4')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningAutocomplete
                idRef={'marketingUsp4Ref'}
                data={data.uniqueSellingPoints}
                inputPlaceholder={translate('select')}
                withoutSearchButton
                isLoading={false}
                multiple={false}
                selectedValues={
                  data.uniqueSellingPoints?.find(
                    (item) => item.lookupItemId === values.marketingUsp4Id
                  ) || null
                }
                displayLabel={(option) => option?.lookupItemName || ''}
                chipsLabel={(option) => option?.lookupItemName || ''}
                getOptionSelected={(option, value) =>
                  option?.lookupItemId === value?.lookupItemId
                }
                onChange={(_e, value) => {
                  updateValues('marketingUsp4Id', value?.lookupItemId);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={styles.marketingDescriptionSection}>
          <Box className={styles.marketingDescriptionFieldWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-description-en')}</span>
            </Box>
            <Box className={styles.marketingDescriptionField}>
              <ReskinningInputs
                idRef='marketing-description-en'
                inputPlaceholder={translate('enter-a-description...')}
                value={values.propertyOverView || ''}
                multiline={true}
                rows={6}
                onInputChanged={(e) => {
                  updateValues('propertyOverView', e.target.value);
                }}
              />
            </Box>
          </Box>
          <Divider />
          <Box className={styles.marketingDescriptionFieldWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('marketing-description-ar')}</span>
            </Box>
            <Box className={styles.marketingDescriptionField}>
              <ReskinningInputs
                idRef='marketingDescriptionArRef'
                inputPlaceholder={translate('enter-a-description...')}
                value={values.arabicPropertyOverview || ''}
                multiline={true}
                rows={6}
                onInputChanged={(e) => {
                  updateValues('arabicPropertyOverview', e.target.value);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={styles.marketingTitleSection}>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('location-insight')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningInputs
                idRef='location-insight'
                inputPlaceholder={translate('enter')}
                value={values.localAreaAndAmenitiesDescription || ''}
                onInputChanged={(e) => {
                  updateValues(
                    'localAreaAndAmenitiesDescription',
                    e.target.value
                  );
                }}
              />
            </Box>
          </Box>
          <Divider />
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('developer-overview')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningInputs
                idRef='developer-overview'
                inputPlaceholder={translate('enter')}
                value={values.developerDescription || ''}
                onInputChanged={(e) => {
                  updateValues('developerDescription', e.target.value);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={styles.marketingTitleSection}>
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('address-line-en')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningInputs
                idRef='address-line-en'
                inputPlaceholder={translate('enter')}
                value={values.englishAddressLine || ''}
                onInputChanged={(e) => {
                  updateValues('englishAddressLine', e.target.value);
                }}
              />
            </Box>
          </Box>
          <Divider />
          <Box className={styles.CPP_itemWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('address-line-ar')}</span>
            </Box>
            <Box className={styles.CPP_fieldWrapper}>
              <ReskinningInputs
                idRef='address-line-ar'
                inputPlaceholder={translate('enter')}
                value={values.arabicAddressLine || ''}
                onInputChanged={(e) => {
                  updateValues('arabicAddressLine', e.target.value);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box className={styles.marketingDescriptionSection}>
          <Box className={styles.marketingDescriptionFieldWrapper}>
            <Box className={styles.CPP_fieldLabel}>
              <span>{translate('additional-notes')}</span>
            </Box>
            <Box className={styles.marketingDescriptionField}>
              <ReskinningInputs
                idRef='additional-notes'
                inputPlaceholder={translate('enter')}
                multiline={true}
                rows={6}
                value={values.propertyAdditionalDescription || ''}
                onInputChanged={(e) => {
                  updateValues('propertyAdditionalDescription', e.target.value);
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomDialog
        open={isOpenAIContentGeneratorDialog}
        title='✨ AI Content Creator'
        onClose={() => {
          setisOpenAIContentGeneratorDialog(false);
          setAiGeneratedContent({
            englishTitle: '',
            englishDescription: '',
            arabicTitle: '',
            arabicDescription: '',
            localAreaAndAmenitiesDescription: '',
            developerDescription: '',
            englishAddressLine: '',
            arabicAddressLine: '',
            additionalText: '',
          });
        }}
        width='fit-content'
        showActions={true}
        confirmText={translate('save') || 'Save'}
        cancelText={translate('cancel') || 'Cancel'}
        onConfirm={() => {
          applyAIContent(aiGeneratedContent);
          setisOpenAIContentGeneratorDialog(false);
        }}
        isDisabled={isGeneratingAI}
        content={
          <Box style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <Box
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('english-title') || 'English Title'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.englishTitle}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      englishTitle: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-title')}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('english-description') || 'English Description'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.englishDescription}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      englishDescription: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-description')}
                  multiline
                  rows={8}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('arabic-title') || 'Arabic Title'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.arabicTitle}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      arabicTitle: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-title')}
                  style={{ direction: 'rtl', width: '100%' }}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('arabic-description') || 'Arabic Description'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.arabicDescription}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      arabicDescription: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-description')}
                  multiline
                  rows={8}
                  style={{ direction: 'rtl', width: '100%' }}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('local-area-description') ||
                    'Local Area Description'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.localAreaAndAmenitiesDescription}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      localAreaAndAmenitiesDescription: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-description')}
                  multiline
                  rows={8}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('developer-description') ||
                    'Developer Description'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.developerDescription}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      developerDescription: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-description')}
                  multiline
                  rows={8}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('english-address') || 'English Address'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.englishAddressLine}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      englishAddressLine: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-address')}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('arabic-address') || 'Arabic Address'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.arabicAddressLine}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      arabicAddressLine: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-address')}
                  style={{ direction: 'rtl', width: '100%' }}
                  disabled={isGeneratingAI}
                />
              </Box>

              <Box>
                <Typography
                  variant='subtitle2'
                  style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  {translate('additional-text-suggestions') ||
                    'Additional Text Suggestions'}
                </Typography>
                <ReskinningInputs
                  value={aiGeneratedContent.additionalText}
                  onInputChanged={(e) =>
                    setAiGeneratedContent({
                      ...aiGeneratedContent,
                      additionalText: e.target.value,
                    })
                  }
                  inputPlaceholder={translate('enter-suggestions')}
                  multiline
                  rows={8}
                  disabled={isGeneratingAI}
                />
              </Box>

              {showRegenerateButton && (
                <Box style={{ marginTop: '16px', textAlign: 'center' }}>
                  <ButtonBase
                    onClick={() => {
                      setShowRegenerateButton(false);
                      setIsGeneratingAI(true);
                      resetAIContent();
                      generateAIContent();
                    }}
                    style={{
                      padding: '10px 24px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      backgroundColor: '#f5f5f5',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#333',
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: '#ebebeb',
                      },
                    }}
                  >
                    {translate('regenerate') || 'Regenerate'}
                  </ButtonBase>
                </Box>
              )}
            </Box>
          </Box>
        }
      />
    </>
  );
}
export default CPP_MarketingAllComponent;
